import apiClient from './client';
import {
  Application,
  ApplicationFilter,
  ApplicationCreateRequest,
  ApplicationUpdateRequest,
  ApplicationListResponse,
  LicenseQuotaInfo,
  LicenseQuotaSyncResponse,
  LicenseUsageDetail,
  ApplicationOwner,
} from '../types/application';
import { SubsclightTenantUsersResponse } from '../types/user';

export const applicationApi = {
  getApplications: async (filter: ApplicationFilter) => {
    const response = await apiClient.get<ApplicationListResponse>('/applications', { params: filter });
    return response.data;
  },

  getApplication: async (id: string) => {
    const response = await apiClient.get<Application>(`/applications/${id}`);
    return response.data;
  },

  createApplication: async (data: ApplicationCreateRequest) => {
    const response = await apiClient.post<Application>('/applications', data);
    return response.data;
  },

  updateApplication: async (id: string, data: ApplicationUpdateRequest) => {
    const response = await apiClient.put<Application>(`/applications/${id}`, data);
    return response.data;
  },

  deleteApplication: async (id: string) => {
    await apiClient.delete(`/applications/${id}`);
  },

  // ライセンス作成用アプリケーション一覧取得
  getApplicationsForLicense: async () => {
    const response = await apiClient.get<{ applications: { id: string; name: string; description: string; status: string; version: string }[] }>('/api/applications/for-license');
    return response.data.applications;
  },

  regenerateApiKey: async (id: string) => {
    const response = await apiClient.post<Application>(`/applications/${id}/regenerate-key`);
    return response.data;
  },

  // 自分のアプリケーション関連API
  getMyApplication: async (): Promise<Application | null> => {
    try {
      const response = await apiClient.get<Application>('/api/my-application');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // アプリケーションが未作成
      }
      throw error;
    }
  },

  createMyApplication: async (data: ApplicationCreateRequest): Promise<Application> => {
    const response = await apiClient.post<Application>('/api/my-application', data);
    return response.data;
  },

  updateMyApplication: async (data: ApplicationUpdateRequest): Promise<Application> => {
    const response = await apiClient.put<Application>('/api/my-application', data);
    return response.data;
  },

  // ライセンス枠管理API
  getLicenseQuota: async (ownerId: string): Promise<LicenseQuotaInfo> => {
    const response = await apiClient.get<LicenseQuotaInfo>(`/api/license-quota/${ownerId}`);
    return response.data;
  },

  syncLicenseQuota: async (ownerId: string): Promise<LicenseQuotaSyncResponse> => {
    const response = await apiClient.post<LicenseQuotaSyncResponse>(`/api/license-quota/${ownerId}/sync`);
    return response.data;
  },

  getLicenseUsageDetail: async (ownerId: string): Promise<LicenseUsageDetail> => {
    const response = await apiClient.get<LicenseUsageDetail>(`/api/license-quota/${ownerId}/usage`);
    return response.data;
  },

  // オーナー情報API
  getMyOwner: async (): Promise<ApplicationOwner | null> => {
    try {
      const response = await apiClient.get<ApplicationOwner>('/api/my-owner');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // オーナー情報が未作成
      }
      throw error;
    }
  },

  createMyOwner: async (data: { subsclight_api_key: string; subsclight_site: string }): Promise<ApplicationOwner> => {
    const response = await apiClient.post<ApplicationOwner>('/api/my-owner', data);
    return response.data;
  },

  updateMyOwner: async (data: { subsclight_api_key?: string; subsclight_site?: string }): Promise<{ message: string }> => {
    const response = await apiClient.put<{ message: string }>('/api/my-owner', data);
    return response.data;
  },

  // サブスクライトテナントユーザー取得API
  getTenantUsers: async (): Promise<SubsclightTenantUsersResponse> => {
    const response = await apiClient.get<SubsclightTenantUsersResponse>('/api/subsclight/tenant-users');
    return response.data;
  },
};

// V2対応のための関数エクスポート
export const getApplications = async (): Promise<Application[]> => {
  const response = await apiClient.get<Application[]>('/applications');
  return response.data;
};

export const createApplication = async (data: ApplicationCreateRequest): Promise<Application> => {
  const response = await apiClient.post<Application>('/applications', data);
  return response.data;
};

// エラークラス
export class ApplicationAPIError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApplicationAPIError';
  }
} 